import { PostHog } from 'posthog-js'
import { addRxPlugin, createRxDatabase, RxDatabase, RxState } from 'rxdb'
import { RxDBCleanupPlugin } from 'rxdb/plugins/cleanup'
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election'
import { RxDBMigrationSchemaPlugin } from 'rxdb/plugins/migration-schema'
import { RxDBStatePlugin } from 'rxdb/plugins/state'
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie'
import { wrappedValidateAjvStorage } from 'rxdb/plugins/validate-ajv'

import {
  ApplicationAuthState,
  ApplicationNetworkState,
  isLoggedIn,
  setupReplication,
} from '@/system/rxdb/replication.js'
import { appUserSchema } from '@/system/rxdb/schemas/appUser.ts'
import { assessmentSchema } from '@/system/rxdb/schemas/assessment.ts'
import { assessmentAdditionalInfoSchema } from '@/system/rxdb/schemas/assessmentAdditionalInfo.ts'
import { assessmentDetailSchema } from '@/system/rxdb/schemas/assessmentDetail.ts'
import { assessmentMeasurement } from '@/system/rxdb/schemas/assessmentMeasurement.ts'
import { signatureSchema } from '@/system/rxdb/schemas/assessmentSignature.ts'
import { assignmentSchema } from '@/system/rxdb/schemas/assignment.ts'

addRxPlugin(RxDBStatePlugin)
addRxPlugin(RxDBMigrationSchemaPlugin)
addRxPlugin(RxDBCleanupPlugin)
addRxPlugin(RxDBLeaderElectionPlugin)

if (process.env.NODE_ENV === 'development') {
  const { RxDBDevModePlugin } = await import('rxdb/plugins/dev-mode')
  addRxPlugin(RxDBDevModePlugin)
}

type DbResolver = {
  db: RxDatabase
  state: RxState<ApplicationAuthState>
  networkState: RxState<ApplicationNetworkState>
  registerOnDeleteHandler: (onDelete: (db: Promise<DbResolver>) => void) => void
}

// We will have a single instance of our database
export let globalDb: Promise<DbResolver> | undefined

const onDbCreation = async (thisDb: RxDatabase, posthog?: PostHog) => {
  await thisDb.addCollections({
    appuser: {
      schema: appUserSchema,
    },
    assignment: {
      schema: assignmentSchema,
    },
    assessment: {
      schema: assessmentSchema,
    },
    assessment_detail: {
      schema: assessmentDetailSchema,
    },
    assessment_additional_info: {
      schema: assessmentAdditionalInfoSchema,
    },
    assessment_measurement: {
      schema: assessmentMeasurement,
    },
    assessment_signature: {
      schema: signatureSchema,
    },
  })

  const state = await thisDb.addState<ApplicationAuthState>()
  const networkState = await thisDb.addState<ApplicationNetworkState>()

  const replications = setupReplication(thisDb, state, networkState, posthog)

  // clear pending authentication / network states after logout
  thisDb.appuser.addHook('post', 'remove', async () => {
    try {
      await state.set('requireAuthentication', () => 'PENDING')
      await state.set('shouldHandleLogout', () => true)
      await networkState.set('lastError', () => ({ error: null }))
    } catch (e) {
      console.error('Unexpected error while updating state', e)
      posthog?.captureException(e)
    }
  })

  let onDeleteCallback: (db: Promise<DbResolver>) => void | undefined

  const logoutHandler = state
    .get$('shouldHandleLogout')
    .subscribe(async (shouldHandleLogout) => {
      if (shouldHandleLogout) {
        await state.set('shouldHandleLogout', () => false)
        const thisIsLoggedIn = await isLoggedIn(thisDb)
        // make sure the user is really not logged in
        if (!thisIsLoggedIn) {
          try {
            // remove all subscriptions
            const unsubscribeReplications = await replications
            await unsubscribeReplications()
            logoutHandler.unsubscribe()
          } catch (e) {
            // This may happen in case we have multiple browsers open and another tab is responsible for managing the replications
            // In this case, it might fail
            if (e instanceof Error) {
              console.warn(`(no error) ${e.message}`, e)
            }
            // reload, because we might be in an unrecoverable state
            location.replace('/')
          }
          try {
            // anyway we want to remove the database!
            // remove the local database
            await thisDb.remove()
          } catch (e) {
            posthog?.captureException(e)
            console.error('Error during cleanup', e)
          }
          // in all cases re-setup database
          globalDb = undefined
          if (onDeleteCallback) {
            onDeleteCallback(createDb(posthog))
          }
        }
      }
    })

  return {
    db: thisDb,
    state,
    networkState,
    registerOnDeleteHandler: (callback: (db: Promise<DbResolver>) => void) => {
      onDeleteCallback = callback
    },
  }
}

export const storage =
  process.env.NODE_ENV === 'development'
    ? wrappedValidateAjvStorage({
        storage: getRxStorageDexie(),
      })
    : getRxStorageDexie()

export const databaseName = 'arzcare'

export function createDb(posthog?: PostHog) {
  if (!globalDb) {
    globalDb = createRxDatabase({
      name: databaseName,
      storage,
      multiInstance: true,
      /** To support hot reload during development, we allow duplicate instances */
      ignoreDuplicate: process.env.NODE_ENV === 'development',
      eventReduce: true,
    }).then((db) => onDbCreation(db, posthog))
  }
  return globalDb
}
